import * as React from "react";
import { cva, type VariantProps } from "class-variance-authority";
import { cn } from "@/shared/lib/utils";
import {
  Avatar as AvatarRoot,
  AvatarImage,
  AvatarFallback,
} from "@/shared/ui/components/ui/avatar";

const avatarVariants = cva(
  "relative inline-flex select-none items-center justify-center  align-middle",
  {
    variants: {
      size: {
        xs: "size-6",
        sm: "size-8",
        md: "size-10",
        lg: "size-12",
        xl: "size-16",
      },
      rounded: {
        none: "rounded",
        sm: "rounded-sm",
        md: "rounded-md",
        lg: "rounded-lg",
        xl: "rounded-xl",
        "2xl": "rounded-2xl",
        "3xl": "rounded-3xl",
        full: "rounded-full",
      },
    },
    defaultVariants: {
      size: "md",
      rounded: "full",
    },
  }
);

const statusIndicatorVariants = cva("absolute", {
  variants: {
    size: {
      xs: "h-1.5 w-1.5",
      sm: "h-2 w-2",
      md: "h-2.5 w-2.5",
      lg: "h-3 w-3",
      xl: "h-3.5 w-3.5",
      "2xl": "h-4 w-4",
    },
    position: {
      top: "top-0 left-1/2 -translate-x-1/2",
      "top-right": "-top-1 -right-1",
      "top-left": "top-0 left-0",
      "bottom-right": "bottom-0 right-0",
      "bottom-left": "bottom-0 left-0",
    },
  },
  defaultVariants: {
    size: "md",
    position: "top-right",
  },
});

export interface AvatarProps
  extends React.HTMLAttributes<HTMLDivElement>,
    VariantProps<typeof avatarVariants> {
  src: string;
  alt?: string;
  text?: string;
  joinDate?: string;
  textClassName?: string;
  joinDateClassName?: string;
  textContainerClassName?: string;
  fallbackClassName?: string;
  fallbackContainerClassName?: string;
  avatarClassName?: string;
  statusClassName?: string;
  isActive?: boolean;
  statusPosition?: VariantProps<typeof statusIndicatorVariants>["position"];
  statusSize?: VariantProps<typeof statusIndicatorVariants>["size"];
  fallback?: React.ReactNode;
}

const Avatar = React.forwardRef<
  React.ElementRef<typeof AvatarRoot>,
  AvatarProps
>(
  (
    {
      className,
      src,
      alt = "Avatar",
      text,
      joinDate,
      textClassName,
      joinDateClassName,
      textContainerClassName,
      fallbackClassName,
      fallbackContainerClassName,
      avatarClassName,
      statusClassName,
      size = "md",
      rounded = "full",
      isActive = false,
      statusPosition = "top-right",
      statusSize,
      fallback,
      ...props
    },
    ref
  ) => {
    const indicatorSize = statusSize ?? size;

    const getFallbackContent = () => {
      if (fallback) return fallback;

      const words = alt.trim().split(/\s+/);
      const initials =
        words.length >= 2 ? words[0][0] + words[1][0] : alt.slice(0, 2);

      return (
        <span className={cn(fallbackClassName)}>{initials.toUpperCase()}</span>
      );
    };

    return (
      <div
        className={cn("inline-flex items-center gap-2", className)}
        {...props}
      >
        <AvatarRoot
          ref={ref}
          className={cn(
            avatarVariants({ size, rounded }),
            "bg-muted",
            avatarClassName
          )}
        >
          <AvatarImage
            className="size-full rounded-[inherit] object-cover"
            src={src}
            alt={alt}
          />
          <AvatarFallback
            className={cn(
              "leading-1 flex size-full items-center justify-center bg-gray-100 text-gray-900 text-[15px] font-medium rounded-[inherit]",
              fallbackContainerClassName
            )}
            delayMs={600}
          >
            {getFallbackContent()}
          </AvatarFallback>
          {isActive && (
            <span
              className={cn(
                statusIndicatorVariants({
                  size: indicatorSize,
                  position: statusPosition,
                }),
                rounded === "full" &&
                  statusPosition === "top-right" &&
                  "-top-0.5 -right-0.5",
                rounded === "full" &&
                  statusPosition === "top-left" &&
                  "top-0right-1",
                "bg-green-400",
                "rounded-full",
                statusClassName
              )}
              aria-label="Active status"
            />
          )}
        </AvatarRoot>
        {(text || joinDate) && (
          <div
            className={cn(
              "flex flex-col gap-1 lg:gap-3",
              textContainerClassName
            )}
          >
            {text && (
              <span
                className={cn(
                  "text-md font-bold text-foreground text-black",
                  textClassName
                )}
              >
                {text}
              </span>
            )}
            {joinDate && (
              <span
                className={cn(
                  "text-md font-medium text-foreground text-gray-500",
                  joinDateClassName
                )}
              >
                {joinDate}
              </span>
            )}
          </div>
        )}
      </div>
    );
  }
);

Avatar.displayName = "Avatar";

export { Avatar, avatarVariants };
